import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAddress, parseEther } from "viem";
import { NetworkType, } from "../../apollo/graphql.generated";
import { envConfig } from "../../config";
import { useMqtt } from "../../features/websocket/WebsocketMqttConnection";
import useEventSubscription from "../../features/websocket/useEventSubscription";
import { apolloClient } from "../../services/apolloClient";
import { chainIdLookup } from "../../utils/blockExplorer";
import { useWatchlistAndFollowContext } from "../watchlist-and-follow/WatchlistAndFollowContext";
import { GetLiveFeedV2Document, GetLiveFeedV3Document, } from "./live.graphql.generated";
const convertChainNameToId = (chain) => {
    const key = chain.toUpperCase().replaceAll("-", "_");
    const network = NetworkType[key];
    if (!network) {
        console.warn("Unknown chain", chain);
        return 0;
    }
    return chainIdLookup(network);
};
function convertV2ToV3(event) {
    var _a, _b, _c, _d;
    const chainId = convertChainNameToId(event.chain);
    return {
        id: `${event.transaction}-${event.token_id}`,
        eventType: event.event_type,
        chainId,
        contractAddress: event.contract_address || "",
        editionId: event.edition_id,
        tokenId: event.token_id,
        token: null,
        collection: event.collection
            ? {
                id: `${chainId}:${event.contract_address || ""}`,
                name: event.collection.name || "",
                image: event.collection.logo || "",
            }
            : null,
        contract: event.collection
            ? {
                ownerAddress: ((_a = event.collection) === null || _a === void 0 ? void 0 : _a.owner_address) || "",
            }
            : undefined,
        amount: event.sale_details
            ? {
                raw: event.sale_details.total_price || "0",
                decimal: parseEther(event.sale_details.total_price || "0"),
                usd: event.sale_details.unit_price_usd_cents
                    ? parseFloat(event.sale_details.unit_price_usd_cents) / 100
                    : undefined,
                native: parseEther(event.sale_details.total_price || "0"),
            }
            : undefined,
        netAmount: undefined,
        from: event.from_address || event.transaction_initiator || "",
        to: event.to_address || "",
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        transactionHash: event.transaction,
        currency: ((_b = event.sale_details) === null || _b === void 0 ? void 0 : _b.payment_token)
            ? {
                contract: event.sale_details.payment_token.address || "",
                name: event.sale_details.payment_token.name || "",
                symbol: event.sale_details.payment_token.symbol || "",
                decimals: event.sale_details.payment_token.decimals || 0,
            }
            : undefined,
        users: (_d = (_c = event.users) === null || _c === void 0 ? void 0 : _c.map((user) => ({
            ownerAddress: user.owner_address || "",
            avatar: user.avatar,
            name: user.name,
            ens: user.ens,
        }))) !== null && _d !== void 0 ? _d : [],
    };
}
const mapMessageToLiveEvent = (message, fromBackend) => {
    if (message.chain_id) {
        const v2Event = { ...message, fromBackend };
        return convertV2ToV3(v2Event);
    }
    const event = message;
    return {
        ...event,
        id: [event.id, event.transactionHash].filter(Boolean).join("-"),
        fromBackend,
    };
};
const satisfiesFilters = (event, { eventTypes, chainId, query, minPriceNative, filterFollowing, filterWatching, }, addressSet) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if ((eventTypes === null || eventTypes === void 0 ? void 0 : eventTypes.length) && !eventTypes.includes(event.eventType)) {
        return false;
    }
    if (chainId && event.chainId !== chainId) {
        return false;
    }
    let keyword = null;
    let address = null;
    if (query) {
        if (isAddress(query)) {
            address = query.toLowerCase();
        }
        else {
            keyword = query.toLowerCase();
        }
    }
    if (keyword && !((_b = (_a = event.collection) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(keyword))) {
        return false;
    }
    if (minPriceNative &&
        parseEther(minPriceNative) >= BigInt((_d = (_c = event.amount) === null || _c === void 0 ? void 0 : _c.raw) !== null && _d !== void 0 ? _d : 0)) {
        return false;
    }
    const addressesToCheck = new Set([
        (_e = event.contractAddress) !== null && _e !== void 0 ? _e : "",
        (_f = event.from) !== null && _f !== void 0 ? _f : "",
        (_g = event.to) !== null && _g !== void 0 ? _g : "",
        (_j = (_h = event.contract) === null || _h === void 0 ? void 0 : _h.ownerAddress) !== null && _j !== void 0 ? _j : "",
    ]
        .filter(Boolean)
        .map((x) => x.toLowerCase()));
    if (address) {
        if (!addressesToCheck.has(address)) {
            return false;
        }
    }
    if (filterFollowing || filterWatching) {
        const onchainId = `${event.chainId}:${event.contractAddress}:${event.editionId}`.toLowerCase();
        if (filterWatching && addressSet.has(onchainId))
            return true;
        if (filterFollowing) {
            // @ts-ignore Set has this method in all browsers
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/intersection
            if (addressSet.intersection(addressesToCheck).size > 0) {
                return true;
            }
            // Testing
            // if (event.transaction_initiator?.startsWith("0x6")) {
            //   console.log("Found in following", event);
            //   return true;
            // }
        }
        return false;
    }
    return true;
};
const tldrEvents = (events, maxCount) => {
    const onchainIdSet = new Set(); // Sets preserve insertion order
    const tldrRecord = {};
    events.forEach((item) => {
        var _a, _b, _c, _d;
        const eventKey = `${item.chainId}:${item.contractAddress}:${item.editionId}-${item.eventType}`;
        const previousCount = (_a = item.tldrCount) !== null && _a !== void 0 ? _a : 1;
        if (tldrRecord[eventKey]) {
            tldrRecord[eventKey].tldrCount =
                previousCount + ((_b = tldrRecord[eventKey].tldrCount) !== null && _b !== void 0 ? _b : 0);
            try {
                const previusAmount = tldrRecord[eventKey].amount;
                if (previusAmount != null && item.amount) {
                    tldrRecord[eventKey].amount = {
                        ...previusAmount,
                        raw: (BigInt(previusAmount.raw) + BigInt(item.amount.raw)).toString(),
                        usd: ((_c = previusAmount.usd) !== null && _c !== void 0 ? _c : 0) + ((_d = item.amount.usd) !== null && _d !== void 0 ? _d : 0),
                    };
                }
            }
            catch (error) { }
            return;
        }
        onchainIdSet.add(eventKey);
        tldrRecord[eventKey] = {
            ...item,
            tldrCount: previousCount,
        };
    });
    return Array.from(onchainIdSet)
        .map((key) => tldrRecord[key])
        .slice(0, maxCount);
};
export default function useLiveEvents(options = {}) {
    var _a, _b, _c, _d, _e, _f;
    const { isWebsocketStopped = false, isWebsocketPaused = false, maxCount = 100, mainnetOnly = true, chainId, eventTypes, query, minPriceNative, filterFollowing = false, filterWatching = false, tldr = false, } = options;
    const filters = useMemo(() => {
        return {
            chainId,
            eventTypes,
            query: query || undefined,
            minPriceNative,
            filterFollowing,
            filterWatching,
            tldr,
        };
    }, [
        chainId,
        eventTypes,
        query,
        minPriceNative,
        filterFollowing,
        filterWatching,
        tldr,
    ]);
    const { client } = useMqtt();
    const { addressSet, followingSet, watchingSet } = useWatchlistAndFollowContext();
    const followingList = useMemo(() => (filters.filterFollowing ? Array.from(followingSet) : undefined), [followingSet, filters.filterFollowing]);
    const watchingList = useMemo(() => (filters.filterWatching ? Array.from(watchingSet) : undefined), [watchingSet, filters.filterWatching]);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [eventData, setEventData] = useState({ queued: [], data: null });
    const [initialData, setInitialData] = useState([]);
    const _isInitialDataLoaded = useRef(false);
    const _isLoading = useRef(false);
    const _isWebsocketPaused = useRef(false);
    _isWebsocketPaused.current = isWebsocketPaused;
    const count = (_b = (_a = eventData.data) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    const queuedCount = (_d = (_c = eventData.queued) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    const canLoadMore = ((_f = (_e = eventData.data) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0) < maxCount;
    const liveFeedTopic = envConfig.websocket.liveFeedTopic;
    const subscribeToLiveFeed = useCallback(() => {
        if (isWebsocketStopped)
            return;
        console.log("Subscribing to live feed topic", liveFeedTopic);
        client === null || client === void 0 ? void 0 : client.subscribe(liveFeedTopic, (error) => {
            setSubscriptionError(error);
        });
        return () => {
            client === null || client === void 0 ? void 0 : client.unsubscribe(liveFeedTopic);
        };
    }, [client, isWebsocketStopped]);
    useEffect(() => {
        return subscribeToLiveFeed();
    }, [subscribeToLiveFeed]);
    const liveEventHandler = useCallback((message) => {
        try {
            if (_isInitialDataLoaded.current === false) {
                return;
            }
            const originalMessage = JSON.parse(message.payload);
            const transformedMessage = mapMessageToLiveEvent(originalMessage);
            if (!satisfiesFilters(transformedMessage, filters, addressSet)) {
                return;
            }
            if (_isWebsocketPaused.current === true) {
                setEventData((prevData) => {
                    return {
                        ...prevData,
                        queued: [...prevData.queued, transformedMessage],
                    };
                });
                return;
            }
            setEventData((prevData) => {
                var _a;
                const reversedQueuedData = prevData.queued.reverse();
                const newData = [
                    transformedMessage,
                    ...reversedQueuedData,
                    ...((_a = prevData.data) !== null && _a !== void 0 ? _a : []),
                ];
                if (tldr === false) {
                    return {
                        queued: [],
                        data: newData.slice(0, maxCount).map((item) => {
                            return {
                                ...item,
                                tldrCount: 0,
                            };
                        }),
                    };
                }
                return {
                    queued: [],
                    data: tldrEvents(newData, maxCount),
                };
            });
        }
        catch (error) {
            console.error("Error parsing message", message, error);
        }
    }, [filters, addressSet]);
    useEventSubscription(liveFeedTopic, liveEventHandler);
    const mergeQueuedEvents = useCallback(() => {
        setEventData((prevData) => {
            var _a;
            const reversedQueuedData = prevData.queued.reverse();
            return {
                queued: [],
                data: [...reversedQueuedData, ...((_a = prevData.data) !== null && _a !== void 0 ? _a : [])].slice(0, maxCount),
            };
        });
    }, [maxCount]);
    const [preLoading, setPreLoading] = useState(false);
    useEffect(() => {
        // console.log("Filters changed", filters);
        setEventData({ queued: [], data: null });
        setInitialData([]);
        _isInitialDataLoaded.current = false;
        _isLoading.current = false;
        setPreLoading(true);
    }, [filters]);
    // v3 is data format, but topic is v2
    const isV3format = envConfig.websocket.liveFeedTopic.includes("-v2-");
    // TODO JJ: We need to rethink this logic. It's a bit convoluted.
    // The filter change, cuses the loadInitialEvent function to rebuild
    // which causes a useEffect in another component to call it, which sets some flags
    // so it cant be called multiple times while one request is in flight,
    // but becuase the useEffect (for filters) above gets envoked, we reset
    // those flags while the request is still in flight.
    //
    // That is why we need the nonce here to keep track, so we can ignore old requests.
    // It would be better if we moved all the logic to this hook, add debuncing (since
    // some filters change in quick succession) and remove the useEffect in the other component.
    // Also rebuilding the array for every event can become expensive and degrade performance.
    // We should switch to a linked list (specifically a FastAccessList)
    const _initialLoadNonce = useRef(0);
    const loadInitialEvents = useCallback(async (limit = maxCount, collectionId) => {
        var _a, _b;
        // If we are filtering by following or watching, we need to make sure
        // we have the data before we continue.
        if (filters.filterFollowing || filters.filterWatching) {
            const canContinueFollowing = filters.filterFollowing && ((_a = followingList === null || followingList === void 0 ? void 0 : followingList.length) !== null && _a !== void 0 ? _a : 0) > 0;
            const canContinueWatching = filters.filterWatching && ((_b = watchingList === null || watchingList === void 0 ? void 0 : watchingList.length) !== null && _b !== void 0 ? _b : 0) > 0;
            // At least one has to have some data
            if (!(canContinueFollowing || canContinueWatching)) {
                console.log("No following/watching list");
                return;
            }
        }
        if (_isLoading.current === true)
            return;
        if (_isInitialDataLoaded.current === true)
            return;
        if (eventData.data != null)
            return;
        _initialLoadNonce.current += 1;
        _isLoading.current = true;
        setPreLoading(false);
        const nonce = _initialLoadNonce.current;
        // console.log(
        //   `🍋 Loading initial events (${nonce})\n`,
        //   JSON.stringify(filters, null, 2)
        // );
        let newEvents = [];
        try {
            const data = await fetchLiveEvents({
                isV3format,
                limit,
                mainnetOnly,
                collectionId,
                ...filters,
                followingList,
                watchingList,
            });
            if (nonce !== _initialLoadNonce.current) {
                // console.log("Ignoring old request", nonce, _initialLoadNonce.current);
                return;
            }
            // Apply filters just in case
            if (filters.tldr) {
                newEvents = tldrEvents(data
                    .map((item) => mapMessageToLiveEvent(item, true))
                    .filter((item) => satisfiesFilters(item, filters, addressSet)), maxCount);
            }
            else {
                newEvents = data
                    .map((item) => mapMessageToLiveEvent(item, true))
                    .filter((item) => satisfiesFilters(item, filters, addressSet));
            }
        }
        catch (error) {
            console.error("Error loading initial events", error);
        }
        setEventData({ data: newEvents, queued: [] });
        setInitialData(newEvents);
        // console.log(
        //   `🍓 Initial events nonce(${nonce}) loaded with size: ${newEvents.length}\n`,
        //   JSON.stringify(filters, null, 2)
        // );
        _isInitialDataLoaded.current = true;
        _isLoading.current = false;
    }, [eventData, maxCount, mainnetOnly, filters, followingList, watchingList]);
    return {
        client,
        data: eventData.data,
        queued: eventData.queued,
        count,
        queuedCount,
        canLoadMore,
        isLoading: _isLoading.current || preLoading,
        isInitialDataLoaded: _isInitialDataLoaded.current,
        initialData,
        subscriptionError,
        subscribeToLiveFeed,
        loadInitialEvents,
        mergeQueuedEvents,
        offersEnabled: isV3format,
    };
}
const fetchLiveEvents = async ({ limit = 100, mainnetOnly = true, isV3format, ...vars }) => {
    const variables = { limit: limit, page: 1, mainnetOnly, ...vars };
    if (!isV3format) {
        const { data } = await apolloClient.query({
            query: GetLiveFeedV2Document,
            variables,
            fetchPolicy: "network-only",
        });
        return data.getLiveFeedV2;
    }
    const { data } = await apolloClient.query({
        query: GetLiveFeedV3Document,
        variables,
        // If we dont use network-only, we get the cached data
        // which can be confusing after already seeing new events
        // come in.
        fetchPolicy: "network-only",
    });
    return data.getLiveFeedV3;
};
